import * as React from "react";

import {
  ApiError,
  getAdminPrompts,
  getLlmRound,
  listLlmRounds,
  previewAdmissionPrompt,
  previewBookingPrompt,
  resetAdminPrompt,
  resumeLlmToolTurn,
  runLlmToolTurn,
  runLlmTurn,
  saveAdminPrompt,
  startLlmRound,
} from "@/api/client";
import type {
  AdminPromptInfo,
  AdmissionPreviewResult,
  LlmExchange,
  LlmRoundSummary,
  LlmStoredTurn,
  LlmToolExchange,
} from "@/api/types";

/**
 * One link per line, `Title — description`.
 *
 * <p>An em dash or a plain hyphen both split it: the phrase the product builds uses an em dash and
 * nobody types one, so accepting only the em dash would quietly turn every typed line into a title
 * with no description and change what the model is shown.</p>
 */
export function parseLinks(text: string): { title: string; description?: string }[] {
  return text
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const at = line.search(/\s+[—-]\s+/);
      if (at < 0) return { title: line };
      return {
        title: line.slice(0, at).trim(),
        description: line.slice(at).replace(/^\s+[—-]\s+/, "").trim() || undefined,
      };
    });
}

const PARSER_KEY = "parser_system";
const BOOKING_KEY = "booking_description_system";
const A2A_KEY = "a2a_admission_system";

/** The sample an admin lands on: two plausible links and a request that is not obviously either. */
const SAMPLE_LINKS = "Recruiters — hiring conversations, engineering roles\nSite visits — coming to look round the workshop\nAdvice — free 20 minutes for founders";
const SAMPLE_REQUEST = "We are growing the backend team and wanted to talk about it";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";

import { useProfile } from "../profile/ProfileContext";

/** Common model ids per provider — the datalist suggestions (custom entry still allowed). */
const MODEL_SUGGESTIONS: Record<string, string[]> = {
  BEDROCK: [
    "us.amazon.nova-lite-v1:0",
    "us.amazon.nova-pro-v1:0",
    "us.amazon.nova-micro-v1:0",
  ],
  OPENAI_COMPAT: ["llama-3.3-70b-versatile"],
  OLLAMA: ["llama3.1:8b"],
};

/** Reverse lookup so picking a known model auto-selects its provider. */
const MODEL_TO_PROVIDER: Record<string, string> = Object.fromEntries(
  Object.entries(MODEL_SUGGESTIONS).flatMap(([p, models]) =>
    models.map((m) => [m, p]),
  ),
);

/** A turn normalised for the transcript, from a live exchange or a stored row. */
interface TurnView {
  key: string;
  provider: string | null;
  model: string | null;
  latencyMs: number | null;
  requestText: string;
  requestJson: unknown;
  responseText: string;
  responseJson: unknown;
  tokens: { input: number | null; output: number | null; total: number | null };
}

function fromExchange(ex: LlmExchange): TurnView {
  return {
    key: `${ex.correlationId}-${ex.seq}`,
    provider: ex.provider,
    model: ex.model,
    latencyMs: ex.latencyMs,
    requestText: `${ex.systemPrompt}\n\n----- user -----\n${ex.userMessage}`,
    requestJson: { systemPrompt: ex.systemPrompt, userMessage: ex.userMessage },
    responseText: ex.rawResponse ?? "(no response, the call failed)",
    responseJson: ex.extractedJson ?? ex.parsed ?? null,
    tokens: { input: ex.inputTokens, output: ex.outputTokens, total: ex.totalTokens },
  };
}

function fromToolExchange(ex: LlmToolExchange): TurnView {
  return {
    key: `${ex.correlationId}-${ex.seq}-tool`,
    provider: ex.provider,
    model: ex.model,
    latencyMs: ex.latencyMs,
    requestText: "(tool-mode loop, see JSON for the conversation)",
    requestJson: ex.conversation,
    responseText: ex.modelJson ?? "(no final answer)",
    responseJson: ex.parsed ?? null,
    tokens: { input: null, output: null, total: null },
  };
}

function fromStored(t: LlmStoredTurn, cid: string): TurnView {
  const req = t.request as { userMessage?: string } | null;
  return {
    key: `${cid}-${t.seq}`,
    provider: t.provider,
    model: t.model,
    latencyMs: t.latencyMs,
    requestText: req?.userMessage ?? "",
    requestJson: t.request,
    responseText: t.rawResponse ?? "(no response)",
    responseJson: t.parsed ?? null,
    tokens: {
      input: t.inputTokens,
      output: t.outputTokens,
      total:
        t.inputTokens != null && t.outputTokens != null
          ? t.inputTokens + t.outputTokens
          : null,
    },
  };
}

const PRE =
  "max-h-80 overflow-auto rounded bg-muted p-3 text-xs font-mono whitespace-pre-wrap";

function Panel({
  title,
  text,
  json,
}: {
  title: string;
  text: string;
  json: unknown;
}) {
  return (
    <div className="space-y-2">
      <div className="text-xs font-semibold uppercase text-muted-foreground">
        {title}
      </div>
      <Tabs defaultValue="text">
        <TabsList>
          <TabsTrigger value="text">Text</TabsTrigger>
          <TabsTrigger value="json">JSON</TabsTrigger>
        </TabsList>
        <TabsContent value="text">
          <pre className={PRE}>{text}</pre>
        </TabsContent>
        <TabsContent value="json">
          <pre className={PRE}>
            {json == null ? "—" : JSON.stringify(json, null, 2)}
          </pre>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function TurnCard({ turn }: { turn: TurnView }) {
  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-center gap-2 space-y-0">
        <Badge variant="secondary">{turn.provider ?? "?"}</Badge>
        {turn.model && (
          <span className="text-xs text-muted-foreground">{turn.model}</span>
        )}
        {turn.tokens.total != null && (
          <Badge variant="outline">
            {turn.tokens.input ?? "?"}→{turn.tokens.output ?? "?"} ·{" "}
            {turn.tokens.total} tok
          </Badge>
        )}
        {turn.latencyMs != null && (
          <Badge variant="outline">{turn.latencyMs} ms</Badge>
        )}
      </CardHeader>
      <CardContent className="grid gap-4 md:grid-cols-2">
        <Panel title="Request" text={turn.requestText} json={turn.requestJson} />
        <Panel title="Response" text={turn.responseText} json={turn.responseJson} />
      </CardContent>
    </Card>
  );
}

function describe(err: unknown): string {
  if (err instanceof ApiError && err.status === 403) {
    return "Your account is not on the admin allowlist.";
  }
  return err instanceof Error ? err.message : String(err);
}

/**
 * Admin-only LLM playground: fire the parser prompt at Groq or Nova, inspect the
 * request/response in text and JSON, and revisit past rounds. Gated on
 * {@code profile.admin}; the backend is the real authority (403 otherwise).
 */
export function AdminLlmPlaygroundPage() {
  const { state } = useProfile();
  const profile = state.status === "ready" ? state.profile : null;
  const isAdmin = profile?.admin === true;

  const [cid, setCid] = React.useState<string | null>(null);
  const [turns, setTurns] = React.useState<TurnView[]>([]);
  const [text, setText] = React.useState("");
  const [tz, setTz] = React.useState("UTC");
  const [provider, setProvider] = React.useState("");
  const [model, setModel] = React.useState("");
  const [temperature, setTemperature] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [toolMode, setToolMode] = React.useState(false);
  const [pending, setPending] = React.useState<LlmToolExchange | null>(null);
  const [resumeAnswer, setResumeAnswer] = React.useState("");
  // DB-backed editable prompts: the fetched list + an editable draft per key.
  const [prompts, setPrompts] = React.useState<AdminPromptInfo[]>([]);
  const [drafts, setDrafts] = React.useState<Record<string, string>>({});
  const [promptKey, setPromptKey] = React.useState(PARSER_KEY);
  const [showPrompt, setShowPrompt] = React.useState(false);
  const [savingPrompt, setSavingPrompt] = React.useState(false);
  // Booking-description prompt preview.
  const [previewTitle, setPreviewTitle] = React.useState("");
  const [previewOut, setPreviewOut] = React.useState<string | null>(null);
  const [previewBusy, setPreviewBusy] = React.useState(false);
  // Agent-request matching: the links to offer, the request, and what came back.
  const [admissionLinks, setAdmissionLinks] = React.useState(SAMPLE_LINKS);
  const [admissionRequest, setAdmissionRequest] = React.useState(SAMPLE_REQUEST);
  const [admissionOut, setAdmissionOut] = React.useState<AdmissionPreviewResult | null>(null);
  const [admissionBusy, setAdmissionBusy] = React.useState(false);

  React.useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    getAdminPrompts()
      .then((list) => {
        if (cancelled) return;
        setPrompts(list);
        setDrafts(Object.fromEntries(list.map((p) => [p.key, p.content])));
      })
      .catch(() => {
        /* leave empty → the parser still falls back to the bundled prompt server-side */
      });
    return () => {
      cancelled = true;
    };
  }, [isAdmin]);

  const [rounds, setRounds] = React.useState<LlmRoundSummary[]>([]);
  const [from, setFrom] = React.useState("");
  const [to, setTo] = React.useState("");

  function newRound() {
    setCid(null);
    setTurns([]);
    setPending(null);
    setResumeAnswer("");
    setError(null);
  }

  function overrides() {
    return {
      provider: provider || undefined,
      model: model.trim() || undefined,
      temperature: temperature ? Number(temperature) : undefined,
      // The parser tester runs against the (unsaved) parser-prompt draft.
      systemPrompt: drafts[PARSER_KEY] || undefined,
    };
  }

  const selectedPrompt = prompts.find((p) => p.key === promptKey);
  const draft = drafts[promptKey] ?? "";
  const dirty = selectedPrompt ? draft !== selectedPrompt.content : false;

  async function savePrompt() {
    if (savingPrompt || !draft.trim()) return;
    setSavingPrompt(true);
    setError(null);
    try {
      const saved = await saveAdminPrompt(promptKey, draft);
      setPrompts((prev) => prev.map((p) => (p.key === saved.key ? saved : p)));
      setDrafts((d) => ({ ...d, [saved.key]: saved.content }));
    } catch (err) {
      setError(describe(err));
    } finally {
      setSavingPrompt(false);
    }
  }

  async function resetPrompt() {
    if (savingPrompt) return;
    setSavingPrompt(true);
    setError(null);
    try {
      const reset = await resetAdminPrompt(promptKey);
      setPrompts((prev) => prev.map((p) => (p.key === reset.key ? reset : p)));
      setDrafts((d) => ({ ...d, [reset.key]: reset.content }));
    } catch (err) {
      setError(describe(err));
    } finally {
      setSavingPrompt(false);
    }
  }

  async function runBookingPreview() {
    if (!previewTitle.trim() || previewBusy) return;
    setPreviewBusy(true);
    setError(null);
    try {
      const { markdown } = await previewBookingPrompt({
        title: previewTitle,
        systemPrompt: drafts[BOOKING_KEY] || undefined,
      });
      setPreviewOut(markdown);
    } catch (err) {
      setError(describe(err));
    } finally {
      setPreviewBusy(false);
    }
  }

  async function runAdmissionPreview() {
    if (!admissionRequest.trim() || admissionBusy) return;
    setAdmissionBusy(true);
    setError(null);
    try {
      // Needs a round to correlate into, same as any other turn.
      const id = cid ?? (await startLlmRound()).correlationId;
      if (!cid) setCid(id);
      const out = await previewAdmissionPrompt(id, {
        links: parseLinks(admissionLinks),
        request: admissionRequest,
        systemPrompt: drafts[A2A_KEY] || undefined,
        provider: provider || undefined,
        model: model.trim() || undefined,
        temperature: temperature ? Number(temperature) : undefined,
      });
      setAdmissionOut(out);
    } catch (err) {
      setError(describe(err));
    } finally {
      setAdmissionBusy(false);
    }
  }

  function absorbTool(ex: LlmToolExchange) {
    if (ex.status === "NEEDS_CLARIFICATION") {
      setPending(ex);
    } else {
      setTurns((prev) => [...prev, fromToolExchange(ex)]);
      setPending(null);
    }
  }

  async function run() {
    if (!text.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      const roundId = cid ?? (await startLlmRound()).correlationId;
      if (!cid) setCid(roundId);
      const req = { text, timeZone: tz || undefined, ...overrides() };
      if (toolMode) {
        absorbTool(await runLlmToolTurn(roundId, req));
      } else {
        const ex = await runLlmTurn(roundId, req);
        setTurns((prev) => [...prev, fromExchange(ex)]);
      }
      setText("");
    } catch (err) {
      setError(describe(err));
    } finally {
      setBusy(false);
    }
  }

  async function resume() {
    if (!pending || !cid || !resumeAnswer.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      absorbTool(
        await resumeLlmToolTurn(cid, {
          answer: resumeAnswer,
          conversation: pending.conversation,
          timeZone: tz || undefined,
          ...overrides(),
        }),
      );
      setResumeAnswer("");
    } catch (err) {
      setError(describe(err));
    } finally {
      setBusy(false);
    }
  }

  async function loadRounds() {
    setError(null);
    try {
      setRounds(
        await listLlmRounds(
          from ? new Date(from).toISOString() : undefined,
          to ? new Date(to).toISOString() : undefined,
        ),
      );
    } catch (err) {
      setError(describe(err));
    }
  }

  async function openRound(id: string) {
    setBusy(true);
    setError(null);
    try {
      const stored = await getLlmRound(id);
      setCid(id);
      setTurns(stored.map((t) => fromStored(t, id)));
    } catch (err) {
      setError(describe(err));
    } finally {
      setBusy(false);
    }
  }

  if (!isAdmin) {
    return (
      <div>
        <main className="mx-auto max-w-2xl p-6">
          <Card>
            <CardHeader>
              <CardTitle>Admin only</CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-muted-foreground">
              Your account is not on the admin allowlist.
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div>
      <main className="mx-auto max-w-5xl space-y-6 p-6">
        <div className="flex items-center justify-between">
          <h1 className="bg-gradient-to-r from-brand-blue via-brand-teal to-brand-coral bg-clip-text font-comic-headline text-2xl uppercase text-transparent">
            LLM playground
          </h1>
          <Button variant="outline" onClick={newRound} disabled={busy}>
            New round
          </Button>
        </div>

        <Card>
          <CardContent className="space-y-3 pt-6">
            <div className="flex flex-wrap gap-3">
              <label className="text-sm">
                Provider
                <select
                  className="ml-2 rounded border bg-background px-2 py-1 text-sm"
                  value={provider}
                  onChange={(e) => setProvider(e.target.value)}
                >
                  <option value="">Deployed default</option>
                  <option value="OPENAI_COMPAT">Groq (OPENAI_COMPAT)</option>
                  <option value="BEDROCK">Nova (BEDROCK)</option>
                  <option value="OLLAMA">Ollama</option>
                </select>
              </label>
              <div className="flex flex-col">
                <Input
                  className="w-72"
                  list="llm-model-ids"
                  placeholder="model, pick or type (e.g. us.amazon.nova-lite-v1:0)"
                  value={model}
                  onChange={(e) => {
                    const v = e.target.value;
                    setModel(v);
                    // Picking a known model selects its provider so the two never mismatch.
                    const p = MODEL_TO_PROVIDER[v.trim()];
                    if (p) setProvider(p);
                  }}
                />
                <datalist id="llm-model-ids">
                  {(provider
                    ? (MODEL_SUGGESTIONS[provider] ?? [])
                    : Object.values(MODEL_SUGGESTIONS).flat()
                  ).map((m) => (
                    <option key={m} value={m} />
                  ))}
                </datalist>
                <a
                  className="text-[11px] text-muted-foreground hover:underline"
                  href="https://docs.aws.amazon.com/bedrock/latest/userguide/models-supported.html"
                  target="_blank"
                  rel="noreferrer"
                >
                  Find more Bedrock models →
                </a>
              </div>
              <Input
                className="w-28"
                placeholder="temp"
                value={temperature}
                onChange={(e) => setTemperature(e.target.value)}
              />
              <Input
                className="w-28"
                placeholder="tz"
                value={tz}
                onChange={(e) => setTz(e.target.value)}
              />
            </div>
            <Textarea
              rows={3}
              placeholder="e.g. cancel my meeting with @priya on tuesday"
              value={text}
              onChange={(e) => setText(e.target.value)}
            />
            <div className="flex items-center gap-3">
              <Button onClick={run} disabled={busy || !text.trim()}>
                {busy ? "Running…" : "Run turn"}
              </Button>
              <label className="flex items-center gap-1 text-sm">
                <input
                  type="checkbox"
                  checked={toolMode}
                  onChange={(e) => setToolMode(e.target.checked)}
                />
                Tool mode
              </label>
              {cid && (
                <span className="text-xs text-muted-foreground">
                  round {cid.slice(0, 8)} · {turns.length} turn(s)
                </span>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="space-y-3">
            <div className="flex flex-row items-center justify-between">
              <CardTitle className="text-base">Prompts</CardTitle>
              <Button variant="outline" onClick={() => setShowPrompt((v) => !v)}>
                {showPrompt ? "Hide" : "Edit"}
              </Button>
            </div>
            <Tabs value={promptKey} onValueChange={setPromptKey}>
              <TabsList>
                {prompts.map((p) => (
                  <TabsTrigger key={p.key} value={p.key}>
                    {p.label}
                    {p.customized && (
                      <span className="ml-1 text-primary" title="customized">
                        •
                      </span>
                    )}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          </CardHeader>
          {showPrompt && (
            <CardContent className="space-y-2">
              {selectedPrompt && (
                <p className="text-xs text-muted-foreground">{selectedPrompt.description}</p>
              )}
              <Textarea
                rows={16}
                className="font-mono text-xs"
                value={draft}
                onChange={(e) => setDrafts((d) => ({ ...d, [promptKey]: e.target.value }))}
                placeholder="(prompt content loads here)"
              />
              <div className="flex flex-wrap items-center gap-2">
                <Button onClick={savePrompt} disabled={savingPrompt || !dirty || !draft.trim()}>
                  {savingPrompt ? "Saving…" : "Save"}
                </Button>
                <Button variant="outline" onClick={resetPrompt} disabled={savingPrompt}>
                  Reset to default
                </Button>
                {dirty && <Badge variant="secondary">unsaved</Badge>}
                {!dirty && selectedPrompt?.customized && <Badge variant="secondary">customized</Badge>}
                {selectedPrompt?.updatedAt && (
                  <span className="text-[11px] text-muted-foreground">
                    saved {new Date(selectedPrompt.updatedAt).toLocaleString()}
                  </span>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                Saved prompts take effect immediately, no deploy. The scheduling prompt is what every
                turn above runs against; the booking prompt drives “Draft with AI”; the matching
                prompt decides which of a host's links a request from outside Scheda is about.
              </p>

              {promptKey === A2A_KEY && (
                <div className="mt-3 space-y-2 rounded-md border p-3">
                  <p className="text-xs font-semibold">
                    Preview: matches a request against links you type, using the text above (unsaved)
                  </p>
                  <p className="text-[11px] text-muted-foreground">
                    One link per line, <code>Title — description</code>. The model fetches these
                    through the same tool production uses and may only answer with one of them, or
                    with nothing.
                  </p>
                  <Textarea
                    rows={4}
                    className="font-mono text-xs"
                    value={admissionLinks}
                    onChange={(e) => setAdmissionLinks(e.target.value)}
                    placeholder="Recruiters — hiring conversations"
                  />
                  <div className="flex gap-2">
                    <Input
                      placeholder="what somebody from outside asked for"
                      value={admissionRequest}
                      onChange={(e) => setAdmissionRequest(e.target.value)}
                    />
                    <Button
                      onClick={runAdmissionPreview}
                      disabled={admissionBusy || !admissionRequest.trim()}
                    >
                      {admissionBusy ? "Matching…" : "Match"}
                    </Button>
                  </div>

                  {admissionOut && (
                    <div className="space-y-2 rounded-md border bg-muted/30 p-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge variant={admissionOut.matched ? "default" : "secondary"}>
                          {admissionOut.matched ? "Matched" : "No match"}
                        </Badge>
                        {/* A model that answered without fetching the candidates is a different
                            failure from one that looked and found nothing. */}
                        {!admissionOut.toolWasCalled && (
                          <Badge variant="secondary" title="it answered without fetching the links">
                            tool not called
                          </Badge>
                        )}
                        {admissionOut.matched && (
                          <Badge variant={admissionOut.wouldAutoBook ? "default" : "secondary"}>
                            {admissionOut.wouldAutoBook ? "could auto-book" : "would ask you first"}
                          </Badge>
                        )}
                        <span className="text-[11px] text-muted-foreground">
                          {admissionOut.model}
                          {admissionOut.latencyMs != null && ` · ${admissionOut.latencyMs}ms`}
                        </span>
                      </div>

                      {admissionOut.matched ? (
                        <p className="text-sm">
                          → <span className="font-medium">{admissionOut.matched}</span>
                        </p>
                      ) : (
                        <p className="text-xs text-muted-foreground">
                          In production this request reaches the host as{" "}
                          <span className="font-medium">Not matched</span>, and nothing is offered.
                          The person is told only “no availability”.
                        </p>
                      )}

                      {/* The distinction that is otherwise invisible: answering with something we
                          rejected is not the same as answering with nothing. */}
                      {!admissionOut.matched && admissionOut.rawAnswer && (
                        <p className="text-[11px] text-muted-foreground">
                          It answered:{" "}
                          <code className="break-all">{admissionOut.rawAnswer}</code>
                        </p>
                      )}

                      <Tabs defaultValue="offered">
                        <TabsList>
                          <TabsTrigger value="offered">Offered</TabsTrigger>
                          <TabsTrigger value="conversation">Conversation</TabsTrigger>
                        </TabsList>
                        <TabsContent value="offered">
                          <Textarea
                            rows={4}
                            readOnly
                            className="font-mono text-[11px]"
                            value={
                              admissionOut.offered.length
                                ? admissionOut.offered.join("\n")
                                : "(nothing — a link with neither title nor description is never offered)"
                            }
                          />
                        </TabsContent>
                        <TabsContent value="conversation">
                          <Textarea
                            rows={10}
                            readOnly
                            className="font-mono text-[11px]"
                            value={admissionOut.conversation
                              .map((m) => `${m.role}: ${m.content}`)
                              .join("\n\n")}
                          />
                        </TabsContent>
                      </Tabs>
                    </div>
                  )}
                </div>
              )}
              {promptKey === BOOKING_KEY && (
                <div className="mt-3 space-y-2 rounded-md border p-3">
                  <p className="text-xs font-semibold">Preview: generates copy from the text above (unsaved)</p>
                  <div className="flex gap-2">
                    <Input
                      placeholder="a sample page title, e.g. 30-min intro call"
                      value={previewTitle}
                      onChange={(e) => setPreviewTitle(e.target.value)}
                    />
                    <Button onClick={runBookingPreview} disabled={previewBusy || !previewTitle.trim()}>
                      {previewBusy ? "Generating…" : "Generate"}
                    </Button>
                  </div>
                  {previewOut != null && (
                    <Textarea rows={6} readOnly className="text-xs" value={previewOut} />
                  )}
                </div>
              )}
            </CardContent>
          )}
        </Card>

        {error && <div className="text-sm text-destructive">{error}</div>}

        {pending && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                Clarification needed (tool mode)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="text-sm">{pending.question}</div>
              <pre className={PRE}>
                {JSON.stringify(pending.conversation, null, 2)}
              </pre>
              <div className="flex items-center gap-2">
                <Input
                  className="max-w-md"
                  placeholder="your answer"
                  value={resumeAnswer}
                  onChange={(e) => setResumeAnswer(e.target.value)}
                />
                <Button onClick={resume} disabled={busy || !resumeAnswer.trim()}>
                  Resume
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="space-y-4">
          {/* Reverse-chronological: the latest turn sits on top. */}
          {[...turns].reverse().map((t) => (
            <TurnCard key={t.key} turn={t} />
          ))}
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Past rounds</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex flex-wrap items-end gap-2">
              <label className="text-xs text-muted-foreground">
                From
                <Input
                  type="datetime-local"
                  className="w-56"
                  value={from}
                  onChange={(e) => setFrom(e.target.value)}
                />
              </label>
              <label className="text-xs text-muted-foreground">
                To
                <Input
                  type="datetime-local"
                  className="w-56"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                />
              </label>
              <Button variant="outline" onClick={loadRounds}>
                Search
              </Button>
            </div>
            <ul className="divide-y text-sm">
              {rounds.map((r) => (
                <li key={r.correlationId}>
                  <button
                    className="w-full py-2 text-left hover:bg-muted"
                    onClick={() => openRound(r.correlationId)}
                  >
                    <span className="font-mono text-xs">
                      {new Date(r.startedAt).toLocaleString()}
                    </span>{" "}
                    · {r.turns} turn(s) · {r.provider ?? "?"} ·{" "}
                    <span className="text-muted-foreground">{r.snippet}</span>
                  </button>
                </li>
              ))}
              {rounds.length === 0 && (
                <li className="py-2 text-muted-foreground">
                  No rounds in range. Run a turn or widen the dates.
                </li>
              )}
            </ul>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
